import { get, isNil } from "lodash";
import { useEffect, useRef, useState } from "react";
import { useMutation } from "shared/hooks/useApi";
import { BUILD_GENERAL_COST } from "utils/api/graphql/mutations/costs";
import { UPDATE_PROJECT } from "utils/api/graphql/mutations/projects";

const COST_DEBOUNCE_MS = 400;
const silent = () => {};

const useCollectiveCost = ({ projectId, fields, ready }) => {
  const [updateProject] = useMutation(UPDATE_PROJECT, { onError: silent });
  const [buildGeneralCost] = useMutation(BUILD_GENERAL_COST, {
    onError: silent,
  });
  const [state, setState] = useState({
    cost: null,
    pending: false,
    failed: false,
  });
  const lastRequest = useRef(0);

  useEffect(() => {
    lastRequest.current += 1;
    const request = lastRequest.current;
    if (!ready) {
      setState({ cost: null, pending: false, failed: false });
      return undefined;
    }
    setState((previous) => ({ ...previous, pending: true, failed: false }));
    const timer = setTimeout(async () => {
      const saved = await updateProject({
        variables: { where: { id: projectId }, data: { fields } },
      });
      const built = isNil(get(saved, "data.updateProject"))
        ? null
        : await buildGeneralCost({
            variables: {
              data: {
                project: { id: projectId },
                insurancePlan: { id: get(fields, "selectedPlan") },
              },
            },
          });
      if (request !== lastRequest.current) return;
      const cost = get(built, "data.buildGeneralCost");
      setState(
        isNil(get(cost, "costDetails"))
          ? { cost: null, pending: false, failed: true }
          : { cost, pending: false, failed: false },
      );
    }, COST_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [projectId, fields, ready, updateProject, buildGeneralCost]);

  return {
    cost: state.cost,
    pending: state.pending,
    loading: state.pending && isNil(state.cost),
    refreshing: state.pending && !isNil(state.cost),
    failed: state.failed,
  };
};

export default useCollectiveCost;
